import type {
  GitignoreSourceInput,
  ParsedGitignoreSource,
  ParsedRule,
  RuleKind,
} from '../../types';
import { normalizeProjectPath, sourceDirectory } from '../path';

/** Parses an ignore source without discarding display-significant whitespace. */
export function parseGitignoreSource(source: GitignoreSourceInput): ParsedGitignoreSource {
  if (typeof source.content !== 'string') {
    throw new TypeError('A .gitignore source must contain text.');
  }

  const path = normalizeProjectPath(source.path, { kind: 'file' });
  if (path.split('/').at(-1) !== '.gitignore') {
    throw new TypeError(`Ignore source \`${path}\` must be named .gitignore.`);
  }

  const directory = sourceDirectory(path);
  const lines = source.content.split(/\r?\n/u);
  const rules = lines.map((raw, index) => parseRule(path, directory, index + 1, raw));

  return { path, directory, content: source.content, rules };
}

export function parseRule(
  source: string,
  sourceDirectoryPath: string,
  line: number,
  raw: string,
): ParsedRule {
  const spacing = trailingSpaceMetadata(raw);
  const effective = spacing.escaped ? raw : raw.slice(0, Math.max(0, raw.length - spacing.count));
  const kind: RuleKind =
    effective.length === 0 ? 'blank' : effective.startsWith('#') ? 'comment' : 'pattern';
  const negated = kind === 'pattern' && effective.startsWith('!');
  const patternBody = negated ? effective.slice(1) : effective;
  const withoutLeadingSlash = patternBody.startsWith('/') ? patternBody.slice(1) : patternBody;
  const withoutBoundarySlashes = withoutLeadingSlash.endsWith('/')
    ? withoutLeadingSlash.slice(0, -1)
    : withoutLeadingSlash;

  return {
    id: `${source}:${String(line)}`,
    source,
    sourceDirectory: sourceDirectoryPath,
    line,
    raw,
    pattern: kind === 'pattern' ? raw : '',
    kind,
    negated,
    directoryOnly: kind === 'pattern' && patternBody.endsWith('/'),
    anchored: kind === 'pattern' && patternBody.startsWith('/'),
    containsSlash: kind === 'pattern' && withoutBoundarySlashes.includes('/'),
    trailingSpaceCount: spacing.count,
    escapedTrailingSpace: spacing.escaped,
  };
}

function trailingSpaceMetadata(raw: string): { readonly count: number; readonly escaped: boolean } {
  let count = 0;
  for (let index = raw.length - 1; index >= 0 && raw[index] === ' '; index -= 1) {
    count += 1;
  }

  if (count === 0) {
    return { count, escaped: false };
  }

  let backslashes = 0;
  for (let index = raw.length - count - 1; index >= 0 && raw[index] === '\\'; index -= 1) {
    backslashes += 1;
  }

  return { count, escaped: backslashes % 2 === 1 };
}
